import type { LoaderFunctionArgs } from "react-router";

import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { GalleryBrowser } from "@/components/ui/GalleryBrowser";
import { PageHeader } from "@/components/ui/PageHeader";
import { Section } from "@/components/ui/Section";
import { fetchGallery, fetchPageData, type PageData } from "@/content";
import { buildMetadata } from "@/lib/seo";
import { contentVersionFrom, usePageData } from "@/page-data";

/**
 * The gallery.
 *
 * The one page that needs every gallery row, so it is the one page that asks
 * for them. They used to travel in every page's hydration payload.
 */
export async function loader({ request, context }: LoaderFunctionArgs): Promise<PageData> {
  const options = { signal: request.signal, version: contentVersionFrom(context) };
  const [page, gallery] = await Promise.all([fetchPageData("gallery", options), fetchGallery(options)]);
  return { ...page, gallery };
}

export const meta = () =>
  buildMetadata({
    title: "Gallery",
    description:
      "Photographs and video from HISCUS programme days in Nepal: women's sessions, education, relief and infrastructure work, public dialogue, Charity Trek and volunteering.",
    path: "/gallery",
  });

function BrowserSection({ section }: SectionProps) {
  const data = usePageData();
  return (
    <Section>
      <GalleryBrowser items={data?.gallery ?? []} />

      {section.texts?.noteTitle || section.texts?.noteBody ? (
        <div className="mt-12 max-w-3xl rounded-[16px] border border-line bg-mist p-6">
          {section.texts?.noteTitle ? <h2 style={{ fontSize: "var(--step-h4)" }}>{section.texts.noteTitle}</h2> : null}
          {section.texts?.noteBody ? <p className="mt-3 text-muted">{section.texts.noteBody}</p> : null}
        </div>
      ) : null}
    </Section>
  );
}

export const gallerySections: SectionRenderers = {
  "gallery.browser": BrowserSection,
};

export function GalleryPage() {
  return (
    <>
      <PageHeader
        eyebrow="Photographs and video"
        title="Gallery"
        lead="Filter by type, programme, place or year. Captions describe what is in the frame; project, place and date are shown wherever HISCUS has published them."
        trail={[
          { name: "Home", href: "/" },
          { name: "Gallery", href: "/gallery" },
        ]}
      />
      <PageSections renderers={gallerySections} />
    </>
  );
}
